import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { teamsApi } from '../../../src/api/client';
import { useEncryptionStore } from '../../../src/stores/encryptionStore';
import { deriveX25519KeyPair, generateTeamKey, wrapTeamKey } from '../../../src/utils/teamCrypto';
import { arrayBufferToBase64 } from '../../../src/utils/encoding';
import { getErrorMessage } from '../../../src/utils/errors';
import { showAlert } from '../../../src/utils/alert';
import { requireConnectivity } from '../../../src/utils/connectivity';
import { useTheme } from '../../../src/theme/ThemeContext';
import { useThemedStyles } from '../../../src/theme/useThemedStyles';
import type { Theme } from '../../../src/theme/types';
import { aesGcmEncrypt, getRandomBytes, getSeed } from '../../../src/utils/crypto';
import ScreenHeader from '../../../src/components/ScreenHeader';

const createStyles = (theme: Theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.bgSecondary,
  },
  content: {
    padding: 24,
  },
  label: {
    fontSize: 14,
    fontWeight: '600' as const,
    color: theme.colors.textMuted,
    marginBottom: 8,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
  input: {
    backgroundColor: theme.colors.surface,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    fontSize: 16,
    color: theme.colors.textPrimary,
    borderWidth: 1,
    borderColor: theme.colors.border,
    marginBottom: 24,
  },
  description: {
    fontSize: 13,
    color: theme.colors.textMuted,
    lineHeight: 18,
    marginBottom: 32,
  },
  createBtn: {
    backgroundColor: theme.colors.primary,
    borderRadius: 10,
    paddingVertical: 14,
    alignItems: 'center' as const,
  },
  createBtnDisabled: {
    opacity: 0.5,
  },
  createBtnText: {
    fontSize: 16,
    fontWeight: '700' as const,
    color: theme.colors.onPrimary,
  },
});

export default function TeamCreateScreen() {
  const styles = useThemedStyles(createStyles);
  const router = useRouter();

  const [teamName, setTeamName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    const trimmed = teamName.trim();
    if (!trimmed || isCreating) return;
    if (!requireConnectivity()) return;

    setIsCreating(true);
    try {
      // 1. Generate a random AES team key
      const teamKey = generateTeamKey();

      // 2. Encrypt the team name with the team key
      const iv = getRandomBytes(12);
      const encoder = new TextEncoder();
      const encryptedName = await aesGcmEncrypt(teamKey, iv, encoder.encode(trimmed));
      const nameEncryptedB64 = arrayBufferToBase64(encryptedName);
      const encryptionMetadata = {
        iv: arrayBufferToBase64(iv),
        alg: 'AES-256-GCM',
      };

      // 3. Create the team on the server
      const teamResponse = await teamsApi.createTeam({
        name_encrypted: nameEncryptedB64,
        encryption_metadata: encryptionMetadata,
      });
      const team = teamResponse.data;

      // 4. Wrap the team key to ourselves.
      //
      // ⚠️  THE X25519 IDENTITY COMES FROM THE SEED, NEVER FROM THE DEVICE KEY.
      //     The device key is the personal team key. A public key derived from
      //     it belongs to a keypair nobody holds the private half of, so a team
      //     key wrapped to it can never be opened — which is what this did until
      //     2026-09: it read `getKeyFromStorage`, called the result `rawSeed`,
      //     and every team it created became unrecoverable on the next launch,
      //     when `syncTeamKeys` failed the GCM tag and swallowed the error. It
      //     only looked like it worked because step 6 keeps the plaintext key in
      //     memory for the rest of the session.
      const encStore = useEncryptionStore.getState();
      const userId = encStore.currentUserId;
      if (!userId) {
        throw new Error('You are not signed in.');
      }
      const seed = await getSeed(userId);
      if (!seed) {
        throw new Error(
          'This device has no key material to share a team with. Restore from your recovery phrase first.'
        );
      }
      const { publicKey } = deriveX25519KeyPair(seed);

      const wrappedKey = wrapTeamKey(teamKey, publicKey);
      const wrappedKeyB64 = arrayBufferToBase64(wrappedKey);

      // 5. Upload the wrapped key
      await teamsApi.uploadKey(team.id, {
        key_version: 1,
        wrapped_key: wrappedKeyB64,
      });

      // 6. Store the team key in memory
      encStore.setTeamKey(team.id, teamKey);

      showAlert('Success', 'Team created successfully!');
      router.back();
    } catch (err) {
      showAlert('Error', getErrorMessage(err, 'Failed to create team.'));
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <View style={styles.container}>
      <ScreenHeader
        title="Create Team"
        left={{
          label: 'Go back',
          icon: 'arrow-back',
          onPress: () => router.back(),
          testID: 'team-create.back-button',
        }}
      />
      <View style={styles.content}>
        <Text style={styles.label}>Team Name</Text>
        <TextInput
          style={styles.input}
          value={teamName}
          onChangeText={setTeamName}
          placeholder="e.g. Project Alpha"
          autoFocus
          returnKeyType="done"
          onSubmitEditing={handleCreate}
          accessibilityLabel="Team name"
          testID="team-create.name-input"
        />

        <Text style={styles.description}>
          Create a shared team to collaborate with others. Team content is
          end-to-end encrypted — only team members can access it. You can
          invite members after creating the team.
        </Text>

        <TouchableOpacity
          style={[styles.createBtn, (!teamName.trim() || isCreating) && styles.createBtnDisabled]}
          onPress={handleCreate}
          disabled={!teamName.trim() || isCreating}
          accessibilityLabel="Create team"
          accessibilityRole="button"
          testID="team-create.submit-button"
        >
          {isCreating ? (
            <ActivityIndicator size="small" color="#fff" />
          ) : (
            <Text style={styles.createBtnText} accessible={false}>Create Team</Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}
